import { AfterViewInit, Directive, ElementRef, forwardRef, HostListener, Input, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import { isNullOrUndefined } from '../../utils/util';

enum TimeInputSection {
  Hours = 1,
  Minutes,
  Meridian
}

enum Meridian {
  AM = 'AM',
  PM = 'PM'
}

@Directive({
  selector: '[uzTimeInput]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimeInputDirective),
      multi: true
    }
  ],
  standalone: true
})
export class TimeInputDirective implements ControlValueAccessor, AfterViewInit {
  @Input() hideClearButton: boolean = false;
  @Input() twentyFourHour: boolean = false;

  // Section Values
  private hours: number;
  private minutes: number;
  private meridian: string;

  private currentSection: TimeInputSection;
  private activeTypeString = '';

  private clearButton: ElementRef;
  private shouldDisableClearButton = new ReplaySubject<boolean>(1);

  constructor(private _renderer: Renderer2, private _elementRef: ElementRef) {
    _renderer.setStyle(_elementRef.nativeElement, 'cursor', 'default');
    _renderer.setStyle(_elementRef.nativeElement, 'caret-color', 'transparent');
  }

  @HostListener('paste', ['$event']) blockPaste(e: KeyboardEvent) {
    e.preventDefault();
  }

  @HostListener('copy', ['$event']) blockCopy(e: KeyboardEvent) {
    e.preventDefault();
  }

  @HostListener('cut', ['$event']) blockCut(e: KeyboardEvent) {
    e.preventDefault();
  }

  @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case 'ArrowUp':
        this.handleArrowUpKeyPress(e);
        break;
      case 'ArrowDown':
        this.handleArrowDownKeyPress(e);
        break;
      case 'ArrowLeft':
        this.handleArrowLeftKeyPress(e);
        break;
      case 'ArrowRight':
        this.handleArrowRightKeyPress(e);
        break;
      case 'a':
      case 'A':
      case 'p':
      case 'P':
        this.handleMeridianKeyPress(e);
        break;
      case 'Tab':
        this.handleTabKeyPress(e);
        break;
      case 'Backspace':
        this.handleBackspaceKeyPress(e);
        break;
      default:
        // TODO: Handle numerics for hours and minutes, after two send to the next section
        if (isNaN(+e.key)) {
          e.preventDefault();
        } else {
          this.handleNumericKeyPress(e);
        }

        break;
    }

    // If the input isn't completed (all sections filled), don't send update events
    const value = this.buildControlValue();
    if (value !== null) {
      this.onChangeCallback(value);
    }
    this.writeValue(this.buildElementValue());
  }

  @HostListener('keyup', ['$event']) onKeyUp(e: KeyboardEvent) {
    e.preventDefault();
    this.selectCurrentSection();
  }

  // I don't think i need blur or focus
  @HostListener('blur', []) onBlur() {
    this.onTouchedCallback();
    this.onChangeCallback(this.buildControlValue());
    this.currentSection = null;
  }

  @HostListener('focus', []) onFocus() {
    this.onTouchedCallback();
    this.selectTimeInputSection(TimeInputSection.Hours);
  }

  @HostListener('mouseup', ['$event']) onMouseup(event: MouseEvent) {
    if (isNullOrUndefined(this.currentSection)) {
      this.selectTimeInputSection(TimeInputSection.Hours);
    } else {
      this.selectCurrentSectionFromCursor();
    }
  }

  onChangeCallback = (_: any) => {};
  onTouchedCallback = () => {};

  ngAfterViewInit() {
    const value = this.buildControlValue();

    // TODO: Phase out the clear button in favor of mat-suffix icons

    if (!this.hideClearButton) {
      // Create a clear button
      this.clearButton = this._renderer.createElement('button');
      this._renderer.setAttribute(
        this.clearButton,
        'style',
        `height: 21px;
        width: 21px;
        background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgCAQAAABIkb+zAAABZUlEQVR4Ae2agWYDQRRFBxCxHxEAFsY8CwsW+r9pAdWgAvmdCttVmJJiIEzFO55wz/zAOWRj5s0kIYQQQgghhBBC/I9xsPc8JYA8lY9xSCy2t4vVcs0ToH+1ahfbs/pnq1Z9E5r+bZ2xhMPuT78lAPq3hMMO0S8nq1a9E5p+W+UEJNjRalstwVW/rWPyJi9l80/I8z39suUlNfCE+XF9W7v6fIKtecb1+QRcn0/A9fkEXJ9PwPXjE/JyT99WQB8Q6Yc7AH6IqD6fgOrzCag+n4Dq8wmYPp/gsAmMTsgvDtvwyAT7idR3OJzw+sxsgdfnE3h9PoHS5xMA/fgA+6IC9BMCPmL9jQL6fAKgXzb7jkxwmHF2ZqraTutAwx8pdaiPH6tosBU/WtRwN368rguO2CsmXfLFX7Pqott3xhmQUN589TsJr8/03GbuP7fRg6foJ2d69NcYh/IZ/+xSCCGEEEIIIYQQvw2UKo1mqKrvAAAAAElFTkSuQmCC);
        background-size: 21px;
        background-position: center center;
        background-color: white;
        border: none;
        background-repeat: no-repeat;
        border-radius: 9px;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 5px;
        display: initial;
        cursor: pointer;`
      );
      this._renderer.setAttribute(this.clearButton, 'type', 'button');
      this._renderer.listen(this.clearButton, 'click', () => {
        this.hours = null;
        this.minutes = null;
        this.meridian = null;
        this.onChangeCallback(this.buildControlValue());
        this.writeValue(null);
        this.selectTimeInputSection(TimeInputSection.Hours);
      });
      this._renderer.insertBefore(this._elementRef.nativeElement.parentNode, this.clearButton, this._elementRef.nativeElement);

      this.shouldDisableClearButton.subscribe(disabled => this._renderer.setProperty(this.clearButton, 'disabled', disabled));

      // If the initialized value is empty, don't show the button
      if (isNullOrUndefined(value) || value === 'undefined:undefined') {
        this._renderer.setStyle(this.clearButton, 'display', 'none');
      }
    }
  }

  writeValue(value: any) {
    /**
     * If a value exists, and it doesn't contain a dash (indicating currently inputting)
     * or an M (indicating it's a formatted element value) then we are working with an inputted
     * model value and need to decompose it into the local state of the directive
     */

    if (value === null) {
      this.hours = null;
      this.minutes = null;
      this.meridian = null;
      value = this.buildElementValue();
    }

    if (value !== null && !isNullOrUndefined(this.clearButton)) {
      this._renderer.setStyle(this.clearButton, 'display', 'initial');
    }

    if (!isNullOrUndefined(value) && value.indexOf('-') === -1 && value.indexOf('M') === -1) {
      value = this.decomposeControlValue(value);
    } else {
      value = this.buildElementValue();
    }

    this._elementRef.nativeElement.value = value;
    this._renderer.setProperty(this._elementRef.nativeElement, 'value', value);
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this._renderer.setProperty(this._elementRef.nativeElement, 'disabled', isDisabled);
    this.shouldDisableClearButton.next(isDisabled);
  }

  private selectCurrentSection() {
    this.selectTimeInputSection(this.currentSection);
  }

  private selectTimeInputSection(timeInputSection: TimeInputSection) {
    let newSelectStart = 0;

    if (timeInputSection === TimeInputSection.Minutes) {
      newSelectStart = 3;
    } else if (timeInputSection === TimeInputSection.Meridian) {
      newSelectStart = 6;
    }

    if (timeInputSection !== this.currentSection) {
      this.activeTypeString = '';
    }
    this._elementRef.nativeElement.setSelectionRange(newSelectStart, newSelectStart + 2);
    this.currentSection = timeInputSection;
  }

  private selectCurrentSectionFromCursor() {
    const cursorPosition: number = +this._elementRef.nativeElement.selectionStart;

    if (cursorPosition > 2 && cursorPosition < 6) {
      this.selectTimeInputSection(TimeInputSection.Minutes);
    } else if (cursorPosition > 5 && cursorPosition < 8) {
      this.selectTimeInputSection(TimeInputSection.Meridian);
    } else {
      this.selectTimeInputSection(TimeInputSection.Hours);
    }
  }

  private getAndSelectCurrentSection(): TimeInputSection {
    this.selectCurrentSection();
    return this.currentSection;
  }

  private getMinHour(): number {
    return this.twentyFourHour ? 0 : 1;
  }

  private getMaxHour(): number {
    return this.twentyFourHour ? 23 : 12;
  }

  private handleArrowUpKeyPress(e: KeyboardEvent) {
    e.preventDefault();

    switch (this.getAndSelectCurrentSection()) {
      case TimeInputSection.Hours:
        if (isNullOrUndefined(this.hours) || this.hours + 1 > this.getMaxHour()) {
          this.hours = this.getMinHour();
          return;
        }
        this.hours++;
        break;
      case TimeInputSection.Minutes:
        if (isNullOrUndefined(this.minutes) || this.minutes + 1 > 59) {
          this.minutes = 0;
          return;
        }
        this.minutes++;
        break;
      case TimeInputSection.Meridian:
        if (isNullOrUndefined(this.meridian) || this.meridian === Meridian.PM) {
          this.meridian = Meridian.AM;
        } else {
          this.meridian = Meridian.PM;
        }
        break;
    }
  }

  private handleArrowDownKeyPress(e: KeyboardEvent) {
    e.preventDefault();

    switch (this.getAndSelectCurrentSection()) {
      case TimeInputSection.Hours:
        if (isNullOrUndefined(this.hours) || this.hours - 1 < this.getMinHour()) {
          this.hours = this.getMaxHour();
          return;
        }
        this.hours--;
        break;
      case TimeInputSection.Minutes:
        if (isNullOrUndefined(this.minutes) || this.minutes - 1 < 0) {
          this.minutes = 59;
          return;
        }
        this.minutes--;
        break;
      case TimeInputSection.Meridian:
        if (isNullOrUndefined(this.meridian) || this.meridian === Meridian.AM) {
          this.meridian = Meridian.PM;
        } else {
          this.meridian = Meridian.AM;
        }
        break;
    }
  }

  private handleArrowRightKeyPress(e: KeyboardEvent) {
    e.preventDefault();

    switch (this.currentSection) {
      case TimeInputSection.Hours:
        this.selectTimeInputSection(TimeInputSection.Minutes);
        break;
      case TimeInputSection.Minutes:
      default:
        if (!this.twentyFourHour) {
          this.selectTimeInputSection(TimeInputSection.Meridian);
        }
        break;
    }
  }

  private handleArrowLeftKeyPress(e: KeyboardEvent) {
    e.preventDefault();

    switch (this.currentSection) {
      case TimeInputSection.Meridian:
        this.selectTimeInputSection(TimeInputSection.Minutes);
        break;
      case TimeInputSection.Minutes:
      default:
        this.selectTimeInputSection(TimeInputSection.Hours);
        break;
    }
  }

  private handleMeridianKeyPress(e: KeyboardEvent) {
    e.preventDefault();

    if (e.key.toLowerCase() === 'a') {
      this.meridian = Meridian.AM;
    } else if (e.key.toLowerCase() === 'p') {
      this.meridian = Meridian.PM;
    }
  }

  private handleBackspaceKeyPress(e: KeyboardEvent) {
    e.preventDefault();

    switch (this.getAndSelectCurrentSection()) {
      case TimeInputSection.Hours:
        this.hours = null;
        break;
      case TimeInputSection.Minutes:
        this.minutes = null;
        this.selectTimeInputSection(TimeInputSection.Hours);
        break;
      case TimeInputSection.Meridian:
        this.meridian = null;
        this.selectTimeInputSection(TimeInputSection.Minutes);
        break;
    }
  }

  private handleTabKeyPress(e: KeyboardEvent) {
    switch (this.currentSection) {
      case TimeInputSection.Hours:
        e.preventDefault();
        this.selectTimeInputSection(TimeInputSection.Minutes);
        break;
      case TimeInputSection.Minutes:
        if (!this.twentyFourHour) {
          e.preventDefault();
          this.selectTimeInputSection(TimeInputSection.Meridian);
        }
        break;
    }
  }

  private handleNumericKeyPress(e: KeyboardEvent) {
    e.preventDefault();

    const maxFirstKey: number = this.twentyFourHour ? 2 : 1;

    switch (this.getAndSelectCurrentSection()) {
      case TimeInputSection.Hours:
        this.activeTypeString += e.key;
        if (+this.activeTypeString.length === 1 && +e.key > maxFirstKey) {
          this.hours = +e.key;
          this.selectTimeInputSection(TimeInputSection.Minutes);
        } else if (+this.activeTypeString > this.getMaxHour()) {
          this.hours = +e.key;
          this.selectTimeInputSection(TimeInputSection.Minutes);
        } else {
          this.hours = +this.activeTypeString;
        }

        if (this.activeTypeString.length >= 2) {
          if (!this.twentyFourHour) {
            if (this.activeTypeString === '00') {
              this.activeTypeString = '12';
              this.hours = 12;
            }
          }

          this.selectTimeInputSection(TimeInputSection.Minutes);
        }
        break;
      case TimeInputSection.Minutes:
        this.activeTypeString += e.key;
        if (+this.activeTypeString.length === 1 && +e.key > 5) {
          this.minutes = +e.key;
          this.selectTimeInputSection(TimeInputSection.Meridian);
        } else if (+this.activeTypeString > 59) {
          this.minutes = +e.key;
          this.selectTimeInputSection(TimeInputSection.Meridian);
        } else {
          this.minutes = +this.activeTypeString;
        }

        if (this.activeTypeString.length >= 2) {
          this.selectTimeInputSection(TimeInputSection.Meridian);
        }
        break;
    }
  }

  private buildControlValue(): string {
    if (!this.isInputValid()) {
      if (!isNullOrUndefined(this.clearButton)) {
        this._renderer.setStyle(this.clearButton, 'display', 'none');
      }

      return null;
    }

    let adjustedHours: number = this.hours;
    let value = '';

    if (!isNullOrUndefined(adjustedHours) && !this.twentyFourHour) {
      if (this.meridian === Meridian.AM && adjustedHours === 12) {
        adjustedHours = 0;
      }
      if (this.meridian === Meridian.PM && adjustedHours !== 12) {
        adjustedHours += 12;
      }
    }

    value += this.formatNumber(adjustedHours);
    value += ':';
    value += this.formatNumber(this.minutes);

    if (!isNullOrUndefined(this.clearButton)) {
      this._renderer.setStyle(this.clearButton, 'display', 'initial');
    }

    return value;
  }

  private decomposeControlValue(value: string): string {
    const twentyFourHourHours = +value.split(':')[0];
    this.hours = twentyFourHourHours;

    if (!this.twentyFourHour) {
      if (twentyFourHourHours === 0 || twentyFourHourHours === 24) {
        this.hours = 12;
        this.meridian = 'AM';
      } else if (twentyFourHourHours === 12) {
        this.hours = 12;
        this.meridian = 'PM';
      } else if (twentyFourHourHours > 12) {
        this.hours = twentyFourHourHours - 12;
        this.meridian = 'PM';
      } else {
        this.hours = twentyFourHourHours;
        this.meridian = 'AM';
      }
    }

    this.minutes = +value.split(':')[1];
    return this.buildElementValue();
  }

  private buildElementValue(): string {
    let value = '';

    value += isNullOrUndefined(this.hours) ? '--' : this.formatNumber(this.hours);
    value += ':';
    value += isNullOrUndefined(this.minutes) ? '--' : this.formatNumber(this.minutes);

    if (!this.twentyFourHour) {
      value += ' ';
      value += isNullOrUndefined(this.meridian) ? '--' : this.meridian;
    }

    return value;
  }

  private formatNumber(num: number): string {
    return (num < 10 ? '0' : '') + num;
  }

  private isInputValid(): boolean {
    if (isNullOrUndefined(this.hours)) {
      return false;
    }

    if (isNullOrUndefined(this.minutes)) {
      return false;
    }

    if (!this.twentyFourHour) {
      if (isNullOrUndefined(this.meridian)) {
        return false;
      }
    }

    return true;
  }
}
